<template>
  <v-card class="section-card" elevation="0">
    <v-form ref="form" class="section-form">

      <div class="form-section">
        <v-row class="form-grid">
          <v-col cols="12" sm="6">
            <span class="detail-text">ชื่อผู้ใช้</span>
            <v-text-field
              v-model.trim="form.username"
              placeholder="@username"
              v-bind="tfProps"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <span class="detail-text">ชื่อร้านค้า</span>
            <v-text-field
              v-model.trim="form.shopName"
              placeholder="ชื่อร้านค้า"
              v-bind="tfProps"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <span class="detail-text">อีเมล</span>
            <v-text-field
              v-model.trim="form.email"
              placeholder="อีเมล"
              v-bind="tfProps"
              :rules="emailRules"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <span class="detail-text">เบอร์โทรศัพท์</span>
            <v-text-field
              v-model.trim="form.phone"
              placeholder="เบอร์โทรศัพท์"
              v-bind="tfProps"
              :rules="phoneRules"
              counter="10"
              @input="onPhoneInput"
            />
          </v-col>
        </v-row>

        <div class="actions">
          <v-btn class="btn-secondary" height="44" outlined @click="reset">
            ยกเลิก
          </v-btn>
          <v-btn class="btn-primary" height="44" @click="save"> บันทึกข้อมูลโปรไฟล์ </v-btn>
        </div>
      </div>
    </v-form>
    <AvatarCropperDialog :file="cropFile" @done="onCropDone" @cancel="cropFile = null" />
  </v-card>
</template>

<script>
import swalAlert from "@/services/alert/swalServices";
import AvatarCropperDialog from "@/components/Shared/AvatarCropperDialog.vue";

const PHONE_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})*$/;

export default {
  name: "SellerInfoCard",
  components: { AvatarCropperDialog },
  props: {
    data: { type: Object, default: () => null },
  },

  data() {
    return {
      form: {
        id: "",
        username: "",
        shopName: "",
        email: "",
        phone: "",
        avatarUrl: "",
      },
      phoneInvalid: false,
      originalData: null,
      photoDialog: false,
      cropFile: null,
      tfProps: { outlined: true, hideDetails: "auto", dense: true },
      selectedAvatarFile: null,
      avatarPreviewUrl: "",
      phoneRules: [
        (v) => !!v || "กรุณาระบุเบอร์โทรศัพท์",
        (v) => /^\d+$/.test(v) || "กรุณาระบุตัวเลขเท่านั้น",
        (v) => v.length === 10 || "กรุณาระบุตัวเลข 10 หลัก",
      ],
      emailRules: [
        (v) =>
          !v ||
          EMAIL_REGEX.test(v) ||
          "กรุณากรอกรูปแบบอีเมลให้ถูกต้อง เช่น name@example.com",
      ],
    };
  },

  computed: {
    initials() {
      if (this.form.shopName) return this.form.shopName.charAt(0).toUpperCase();
      if (this.form.username) return this.form.username.charAt(0).toUpperCase();
      return "";
    },
    currentAvatarUrl() {
      if (this.avatarPreviewUrl) return this.avatarPreviewUrl;
      const raw = this.form.avatarUrl;
      if (!raw) return "";
      if (/^https?:\/\//i.test(raw) || raw.startsWith("blob:")) return raw;
      const base = process.env.VUE_APP_API_URL || "";
      if (!base) return raw;
      const cleanBase = base.replace(/\/+$/, "");
      const cleanPath = raw.startsWith("/") ? raw : `/${raw}`;
      return `${cleanBase}${cleanPath}`;
    },
  },

  watch: {
    data: {
      immediate: true,
      handler(v) {
        if (!v) return;
        this.form = {
          id: v.id || "",
          username: v.username || "",
          shopName: v.shopName || "",
          email: v.email || "",
          phone: v.phone || "",
          avatarUrl: v.storeImage || "",
        };
        this.originalData = JSON.parse(JSON.stringify(this.form));
      },
    },
  },

  methods: {
    async reset() {
      if (!this.originalData) return;
      const res = await swalAlert.Dialog(
        "ยกเลิกการแก้ไขข้อมูล",
        "คุณต้องการยืนยันยกเลิกการแก้ไขข้อมูลใช่หรือไม่?",
        require("@/assets/alert/cancel.png"),
        120,
        120,
        "ยืนยัน",
        "ยกเลิก",
        true
      );
      if (!res.isConfirmed) return;
      this.form = JSON.parse(JSON.stringify(this.originalData));
      if (this.avatarPreviewUrl) URL.revokeObjectURL(this.avatarPreviewUrl);
      this.avatarPreviewUrl = "";
      this.selectedAvatarFile = null;
    },

    async save() {
      if (this.phoneInvalid) {
        await swalAlert.Error("เบอร์โทรศัพท์ต้องเป็นตัวเลขเท่านั้น");
        return;
      }
      const phone = (this.form.phone || "").trim();
      if (phone && !PHONE_REGEX.test(phone)) {
        await swalAlert.Error("เบอร์โทรศัพท์ไม่ถูกต้อง");
        return;
      }
      const formRef = this.$refs.form;
      if (formRef?.validate && !formRef.validate()) return;
      this.$emit("save", { ...this.form });
    },

    onAvatarClick() {
      if (this.$refs.avatarInput) this.$refs.avatarInput.click();
    },

    onAvatarSelected(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      this.cropFile = file;
    },

    onCropDone(croppedFile) {
      this.cropFile = null;
      if (this.avatarPreviewUrl) URL.revokeObjectURL(this.avatarPreviewUrl);
      this.selectedAvatarFile = croppedFile;
      this.avatarPreviewUrl = URL.createObjectURL(croppedFile);
      this.$emit("avatar-change", croppedFile);
    },

    onAvatarError() {
      // server URL ใช้ไม่ได้ แต่ยัง avatarPreviewUrl อยู่ → ไม่ทำอะไร
      if (!this.avatarPreviewUrl) {
        this.form.avatarUrl = "";
      }
    },

    onPhoneInput(e) {
      const raw = e.target.value;
      const digitsOnly = raw.replace(/\D/g, "");
      this.phoneInvalid = raw !== digitsOnly;
      this.form.phone = digitsOnly.slice(0, 10);
    },
  },
};
</script>

<style scoped>
.section-card {
  border-radius: 18px;
  border: 1px solid #e5e7f0;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.03);
}
.section-form {
  padding: 24px;
}
.profile-hero {
  border-radius: 14px;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  margin-bottom: 24px;
}
.profile-hero-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.avatar-wrapper {
  position: relative;
  margin-bottom: 16px;
  cursor: pointer;
}
.avatar-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #e5e7eb;
  box-shadow: 0 2px 12px rgba(0,0,0,0.10);
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 50%;
}
.avatar-text {
  font-size: 28px;
  font-weight: 700;
  color: #2563eb;
}
.avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 50%;
}
.avatar-circle:hover .avatar-overlay {
  opacity: 1;
}
.avatar-input { display: none; }

.profile-name {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}
.profile-username {
  color: #64748b;
  font-size: 14px;
  margin-top: 4px;
}
.role-pill {
  margin-top: 12px;
  padding: 6px 16px;
  background: #dcfce7;
  color: #166534;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.form-section {
  border-top: 1px solid #f1f5f9;
  padding-top: 24px;
}
.detail-text {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}
.btn-secondary {
  border-radius: 8px !important;
  text-transform: none;
}
.btn-primary {
  border-radius: 8px !important;
  background: #2563eb !important;
  color: white !important;
  text-transform: none;
}
</style>
